import React, { memo, useEffect, useRef } from "react";
import type { CustomCellEditorProps } from "ag-grid-react";
import type { Column, GridApi, IRowNode } from "ag-grid-community";
import { renderFindHighlight } from "./SnGridFindHighlight";

/**
 * SnGrid 한글 첫 글자 입력 (26.10.21)
 *
 * 셀을 선택만 한 상태에서는 입력칸이 없어서 첫 키를 한글 입력기가 아니라 그리드가 받는다.
 * 그래서 ㄱ 이 r 로 들어가고 두 번째 키부터 한글이 된다. 브라우저 동작이라 AG Grid 는 고치지 않는다.
 *
 * 선택된 글자 칸 안에 보이지 않는 입력칸을 하나 깔고 포커스를 거기에 둔다.
 * - 한글 조합이 시작되면 편집을 열고 그 입력칸을 그대로 편집칸으로 쓴다.
 *   포커스를 다른 입력칸으로 옮기면 조합이 끊겨 자모가 갈라지므로 옮기지 않는다.
 * - 영문 · 숫자는 지금처럼 그리드 기본 편집기로 연다. Space 는 SnGrid 키 처리가 그대로 연다.
 * - 입력칸이 셀 안에 있으므로 방향키 · Enter · Tab · Delete · 복사는 그리드가 그대로 받는다.
 *
 * 글자 칸 값이 문자열 그대로 그려지면 React 가 셀 내용을 통째로 갈아 끼워 입력칸이 지워진다.
 * 그래서 편집 가능한 그리드의 글자 칸에는 같은 모양의 렌더러(찾기 하이라이트 포함)를 붙인다.
 *
 * 숨은 입력칸 클래스(sn-ime-input)는 useGridPaste 에서도 본다. 이름을 바꾸면 같이 바꾼다.
 */

const TEXT_EDITOR = "agTextCellEditor";
const INPUT_CLASS = "sn-ime-input";
const EDITING_CLASS = "sn-ime-input--editing";

type ImeMode = "idle" | "opening" | "editing";

/** 한글 조합으로 연 편집 한 번 */
interface ImeSession {
  node: IRowNode;
  column: Column;
  input: HTMLInputElement;
  begin: () => void;
  end: () => void;
}

/** 글자 칸 렌더러 — 기본 표시(포맷 값)와 같고 찾기 하이라이트를 입힌다 */
const ImeTextCellRenderer = (params: any) =>
  renderFindHighlight(params, params.valueFormatted ?? params.value);

/** 한글 조합으로 연 편집기. 자기 입력칸을 만들지 않고 숨은 입력칸을 그대로 편집칸으로 쓴다 */
const ImeTextCellEditor = memo(
  (props: CustomCellEditorProps & { snIme: ImeSession }) => {
    const { snIme, onValueChange } = props;

    useEffect(() => {
      const { input } = snIme;
      const sync = () => onValueChange(input.value);
      input.addEventListener("input", sync);
      input.addEventListener("compositionend", sync);
      snIme.begin();
      sync();
      return () => {
        input.removeEventListener("input", sync);
        input.removeEventListener("compositionend", sync);
        snIme.end();
      };
    }, []);

    return null;
  }
);

export const useSnGridIme = (
  gridApiRef: React.MutableRefObject<GridApi | null | undefined>,
  gridDomRef: React.RefObject<HTMLDivElement>,
  isEditable: boolean
) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const modeRef = useRef<ImeMode>("idle");
  const targetRef = useRef<{ node: IRowNode; column: Column } | null>(null); // 입력칸이 깔린 셀
  const sessionRef = useRef<ImeSession | null>(null);
  // 글자 칸 colId → 원래 cellEditorSelector (없으면 undefined)
  const textColumnsRef = useRef(new Map<string, ((p: any) => any) | undefined>());

  const getApi = () => {
    const api = gridApiRef.current;
    return api && !api.isDestroyed() ? api : null;
  };

  // 편집칸으로 쓰던 입력칸을 다시 숨은 입력칸으로 되돌린다
  const release = () => {
    modeRef.current = "idle";
    sessionRef.current = null;
    const input = inputRef.current;
    if (!input) return;
    input.classList.remove(EDITING_CLASS);
    input.value = "";
  };

  // 이 셀이 글자 편집기로 열리는 셀인지 (원래 selector 가 숫자 · 체크박스를 고르는 행은 제외)
  const isTextCell = (node: IRowNode, column: Column) => {
    const colId = column.getColId();
    if (!textColumnsRef.current.has(colId)) return false;
    if (!column.isCellEditable(node)) return false;
    const colDef: any = column.getColDef();
    const selector = textColumnsRef.current.get(colId);
    const picked = selector?.({ data: node.data, node, column, colDef });
    return (picked?.component ?? colDef.cellEditor ?? TEXT_EDITOR) === TEXT_EDITOR;
  };

  // 포커스를 받은 셀이 글자 칸이면 숨은 입력칸을 그 안에 깔고 포커스를 옮긴다
  const attach = (cell: HTMLElement) => {
    const api = getApi();
    const input = inputRef.current;
    if (!api || !input) return;
    if (api.getEditingCells().length > 0) return;
    // 앞 편집이 끝났는데 편집기 정리가 아직 안 왔으면 여기서 정리한다
    if (modeRef.current !== "idle") release();

    const focused = api.getFocusedCell();
    if (!focused || focused.rowPinned) return;
    if (cell.getAttribute("col-id") !== focused.column.getColId()) return;
    if (
      cell.closest(".ag-row")?.getAttribute("row-index") !==
      String(focused.rowIndex)
    )
      return;
    const node = api.getDisplayedRowAtIndex(focused.rowIndex);
    if (!node || node.group || !isTextCell(node, focused.column)) return;

    targetRef.current = { node, column: focused.column };
    input.value = "";
    if (input.parentElement !== cell) cell.appendChild(input);
    input.focus({ preventScroll: true });
  };

  // 숨은 입력칸이 깔린 셀의 편집을 연다. key 가 있으면 그 글자로 시작한다(그리드 기본 동작과 같다)
  const startEdit = (key?: string) => {
    const api = getApi();
    const target = targetRef.current;
    if (!api || !target || target.node.rowIndex == null) return false;
    api.startEditingCell({
      rowIndex: target.node.rowIndex,
      colKey: target.column,
      key,
    });
    return api.getEditingCells().length > 0;
  };

  useEffect(() => {
    const root = gridDomRef.current;
    if (!isEditable || !root) return;

    const input = document.createElement("input");
    input.type = "text";
    input.className = INPUT_CLASS;
    input.tabIndex = -1;
    input.autocomplete = "off";
    input.spellcheck = false;
    input.setAttribute("aria-hidden", "true");
    inputRef.current = input;

    const onFocusIn = (e: FocusEvent) => {
      const cell = e.target as HTMLElement;
      if (cell?.classList?.contains("ag-cell")) attach(cell);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (modeRef.current !== "idle") return; // 편집칸으로 쓰는 중에는 일반 편집기와 같다
      if (e.isComposing || e.keyCode === 229) return; // 한글 조합은 compositionstart 에서 연다
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key.length !== 1 || e.key === " ") return;
      // 그리드는 셀 안쪽 요소에서 온 글자 키로는 편집을 열지 않으므로 여기서 기본 편집기를 연다
      e.preventDefault();
      startEdit(e.key);
    };

    const onCompositionStart = () => {
      if (modeRef.current !== "idle" || !targetRef.current) return;
      const session: ImeSession = {
        ...targetRef.current,
        input,
        begin: () => {
          if (sessionRef.current === session) modeRef.current = "editing";
        },
        end: () => {
          if (sessionRef.current === session) release();
        },
      };
      sessionRef.current = session;
      modeRef.current = "opening";
      input.classList.add(EDITING_CLASS); // 조합 중인 글자가 바로 보이게
      if (!startEdit()) release();
    };

    const onCompositionEnd = () => {
      // 편집을 열지 못한 채 조합만 끝났으면 남은 글자를 비운다
      if (modeRef.current !== "idle") return;
      window.setTimeout(() => {
        if (modeRef.current === "idle") input.value = "";
      }, 0);
    };

    const onPaste = (e: ClipboardEvent) => {
      // 붙여넣기는 그리드(Ctrl+V)가 클립보드에서 직접 읽는다. 숨은 입력칸에는 글자가 들어가지 않게 한다
      if (modeRef.current === "idle") e.preventDefault();
    };

    const onBlur = () => {
      // 다른 종류의 편집기가 열려 포커스를 가져갔으면 되돌린다
      if (modeRef.current === "opening") release();
      else if (modeRef.current === "idle") input.value = "";
    };

    root.addEventListener("focusin", onFocusIn);
    input.addEventListener("keydown", onKeyDown);
    input.addEventListener("compositionstart", onCompositionStart);
    input.addEventListener("compositionend", onCompositionEnd);
    input.addEventListener("paste", onPaste);
    input.addEventListener("blur", onBlur);

    return () => {
      root.removeEventListener("focusin", onFocusIn);
      input.remove();
      inputRef.current = null;
      release();
    };
  }, [isEditable]);

  /**
   * 컬럼 정의에 한글 첫 글자 처리를 입힌다. 글자 편집기로 열릴 수 있는 칸만 대상이다.
   * - cellEditorSelector: 한글 조합으로 연 편집이면 숨은 입력칸을 쓰는 편집기, 아니면 원래대로
   * - cellRenderer: 없으면 글자 칸 렌더러 (숨은 입력칸이 지워지지 않게)
   */
  const applyToColumn = (colDef: any): any => {
    if (!isEditable || !colDef) return colDef;
    if (Array.isArray(colDef.children)) {
      return { ...colDef, children: colDef.children.map(applyToColumn) };
    }
    if (colDef.editable === false) return colDef;
    const original = colDef.cellEditorSelector;
    const mayBeText =
      !!original || !colDef.cellEditor || colDef.cellEditor === TEXT_EDITOR;
    const colId = colDef.colId ?? colDef.field;
    if (!mayBeText || !colId) return colDef;

    textColumnsRef.current.set(colId, original);
    return {
      ...colDef,
      cellEditorSelector: (params: any) => {
        const session = sessionRef.current;
        if (
          session &&
          session.node === params.node &&
          session.column === params.column
        ) {
          return { component: ImeTextCellEditor, params: { snIme: session } };
        }
        // undefined 를 돌려주면 ag-grid 는 colDef 의 cellEditor 로 돌아간다
        return original ? original(params) : undefined;
      },
      cellRenderer: colDef.cellRenderer ?? ImeTextCellRenderer,
    };
  };

  return { applyToColumn };
};
